import { Permissions, SocieteAvecPermissionsPortailClientDto, TypeUtilisateurRuunui } from "@lexi-clients/lexi";
import { lastValueFrom } from "rxjs";
import { DynamicNavigationMenuService } from "../services/dynamic-navigation-menu.service";
import { FeatureFlagService } from "../shared/services/feature-flag.service";

export interface Navigation {
    icon: string;
    text: string;
    path: string | null;
    permissionsRequises: Permissions[];
    visible: boolean;
    expanded?: boolean;
    items: Navigation[];
}

const donneesBaseNavigation = (enableTransporteurs: boolean): Navigation => {
    const donneesBase: Navigation = {
        text: 'Données de base',
        path: '',
        icon: 'optionsgear',
        permissionsRequises: [],
        visible: true,
        items: [
            { text: 'Clients', path: '/clients', permissionsRequises: [], visible: true, items: [] } as Navigation,
            { text: 'Articles', path: '/articles', permissionsRequises: [], visible: true, items: [] } as Navigation,
            { text: 'Fournisseurs', path: '/fournisseurs', permissionsRequises: [], visible: true, items: [] } as Navigation,
            { text: 'Contacts', path: '/contacts', permissionsRequises: [], visible: true, items: [] } as Navigation,
            { text: 'Classifications articles', path: '/classifications/articles', permissionsRequises: [Permissions.canAdministrerArticles], visible: true, items: [] } as Navigation,
        ]
    };

    if (enableTransporteurs) {
        donneesBase.items.push(
            { text: 'Transporteurs', path: '/transporteurs', permissionsRequises: [Permissions.canGererTransporteurs], visible: true, items: [] } as Navigation
        );
    }

    return donneesBase;
}

export interface NavigationTop {
    id: number;
    title: string;
    navigation: Navigation[];
    visible: boolean;
}

export enum NavigationCollaborateurMenuId {
    vente = 0,
    logistique = 2,
    sav = 3,
    douane = 4,
    fretLocal = 5,
    pointDeVente = 6,
    kpi = 7,
    administration = 8,
    comptabilite = 9,
    support = 10
}

export class Parametrage {
    currentUserType: TypeUtilisateurRuunui;
    currentUserCanGererTransporteurs: boolean;
    /** true si la société en cours gère du stock */
    currentSocieteManageStock: boolean;
}

export const navigationCollaborateur = async (parametrage: Parametrage, featureFlagService: FeatureFlagService, navigationService: DynamicNavigationMenuService): Promise<Array<NavigationTop>> => {
    const enableTransporteurs: boolean = parametrage.currentUserCanGererTransporteurs && parametrage.currentUserType == TypeUtilisateurRuunui.chargeur;

    // Référence : activite-list-grid.component.ts
    const DATAGRID_CONF_STORAGE_KEY_BASE = "grille_activites";
    const navigationItemsActivites = await lastValueFrom(navigationService.getNavigationMenuByDatagridStoragekey(DATAGRID_CONF_STORAGE_KEY_BASE));

    return [
        {
            id: NavigationCollaborateurMenuId.vente,
            title: 'Vente',
            visible: false,
            navigation: [
                { text: 'Site', path: '/site-en-cours', icon: 'home', permissionsRequises: [Permissions.canManageSite], visible: true, items: [] } as Navigation,
                { text: 'Clients', path: '/clients', icon: 'user', permissionsRequises: [], visible: true, items: [] } as Navigation,
                { text: 'Articles', path: '/articles', icon: 'product', visible: true, permissionsRequises: [], items: [] } as Navigation,
                {
                    text: 'Transactions', path: null, icon: 'file', visible: true, permissionsRequises: [], items: [
                        { text: 'Pièces', path: '/vente/pieces', visible: true, permissionsRequises: [], items: [] } as Navigation,
                        { text: 'Règlements', path: '/vente/reglements', visible: true, permissionsRequises: [], items: [] } as Navigation,
                    ]
                } as Navigation,
                {
                    text: 'Stock', path: null, icon: 'mediumiconslayout', visible: parametrage.currentSocieteManageStock, permissionsRequises: [], items: [
                        { text: 'État', path: '/visualisation-stock', visible: parametrage.currentSocieteManageStock, permissionsRequises: [Permissions.accesVueConsultationStock], items: [] } as Navigation,
                        { text: 'Mouvements', path: '/visualisation-des-mouvements', visible: parametrage.currentSocieteManageStock, permissionsRequises: [Permissions.accesVueConsultationStock], items: [] } as Navigation,
                        { text: 'Bons', path: '/visualisation-des-bons', visible: parametrage.currentSocieteManageStock, permissionsRequises: [], items: [] } as Navigation,
                        { text: 'Valorisation stock', path: '/valorisation-stock', visible: parametrage.currentSocieteManageStock, permissionsRequises: [Permissions.accesVueConsultationStock, Permissions.canAfficherPmp], items: [] } as Navigation,
                    ]
                } as Navigation,
                { text: 'Éditions', path: '/vente/editions', icon: 'paste', visible: true, permissionsRequises: [Permissions.accesEditionsTpv], items: [] } as Navigation,
                { text: 'Statistiques', path: '/vente/statistiques-site', icon: 'paste', visible: true, permissionsRequises: [Permissions.canAfficherStatistiqueTpv], items: [] } as Navigation,
                {
                    text: 'Caisse', path: null, icon: 'icon ion-md-desktop', visible: true, permissionsRequises: [], items: [
                        { text: 'Contrôle', path: '/vente/controle-de-periode', visible: true, permissionsRequises: [Permissions.canCloturerCaisse], items: [] } as Navigation,
                        { text: 'Gestion des caisses', path: '/vente/caisses', visible: true, permissionsRequises: [Permissions.canManageTerminaux], items: [] } as Navigation,
                        { text: 'Raccourcis', path: '/vente/raccourcis', visible: true, permissionsRequises: [Permissions.canGererRaccourcis], items: [] } as Navigation,
                    ]
                } as Navigation,
                {
                    text: 'Gestion', path: null, icon: 'folder', visible: true, permissionsRequises: [], items: [
                        { text: 'Bons d\'achat', path: '/vente/bons-achat', permissionsRequises: [Permissions.administrationBonsAchat], visible: true, items: [] } as Navigation,
                        { text: 'Formules', path: '/formules', permissionsRequises: [Permissions.canAdministrerArticles], visible: true, items: [] } as Navigation,
                        { text: 'Listing articles', path: '/listing-articles', permissionsRequises: [Permissions.canAdministrerArticles], visible: true, items: [] } as Navigation,
                        { text: 'Opérations commerciales', path: '/vente/operations-commerciales', visible: true, permissionsRequises: [Permissions.administrationOperationsCommerciales], items: [] } as Navigation,
                        { text: 'Tarifications', path: '/vente/tarifications', permissionsRequises: [Permissions.canModifierTarifs], visible: true, items: [] } as Navigation,
                    ]
                } as Navigation,
                { text: 'Synchronisation Bo', path: '/vente/synchronisation-back-office', icon: 'download', visible: true, permissionsRequises: [Permissions.canSynchroniserBackoffice], items: [] } as Navigation
            ]
        },
        {
            id: NavigationCollaborateurMenuId.logistique,
            title: 'Logistique',
            visible: false,
            navigation: navigationLogistique(parametrage, enableTransporteurs, featureFlagService)
        },
        {
            id: NavigationCollaborateurMenuId.sav,
            title: 'Sav',
            visible: false,
            navigation: [
                // { text: 'Commun', path: '', icon: 'folder', permissionsRequises: [], visible: true, items: [
                //     { text: 'Clients', path: '/sav/clients', icon: 'folder', permissionsRequises: [], visible: true, items: [] } as Navigation,
                //     { text: 'Produits', path: '/sav/articles', icon: 'folder', permissionsRequises: [], visible: true, items: [] } as Navigation,
                // ] } as Navigation,
                { text: 'Demandes', path: '/sav/demandes', icon: 'help', permissionsRequises: [], visible: true, items: [] } as Navigation,
                { text: 'Modèles', path: '/sav/modeles', icon: 'product', permissionsRequises: [], visible: true, items: [] } as Navigation,
                { text: 'Activités', path: '/sav/activites', icon: 'runner', permissionsRequises: [], visible: true, items: navigationItemsActivites } as Navigation,
                { text: 'Dossiers', path: '/sav/dossiers', icon: 'folder', permissionsRequises: [], visible: true, items: [] } as Navigation,
                // { text: 'Interventions', path: '/sav/interventions', icon: 'user', visible: true, permissionsRequises: [], items: [] } as Navigation,
                { text: 'Parc', path: '/sav/parcs', icon: 'product', permissionsRequises: [], visible: true, items: [] } as Navigation,
                // { text: 'Contrats', path: '/sav/contrats', icon: 'user', visible: true, permissionsRequises: [], items: [] } as Navigation,
                // { text: 'Paramètres', path: '', icon: 'folder', visible: true, permissionsRequises: [], items: [
                //     { text: 'Type de contrats', path: '/sav/contrat-types', visible: true, icon: 'user', permissionsRequises: [], items: [] } as Navigation,
                //     { text: 'Type d\'évènements', path: '/sav/evenement-types', visible: true, icon: 'user', permissionsRequises: [], items: [] } as Navigation,
                //     { text: 'Flux', path: '/sav/flux', icon: 'user', visible: true, permissionsRequises: [], items: [] } as Navigation,
                //     { text: 'Caractéristiques', path: '/sav/caracteristiques', icon: 'user', visible: true, permissionsRequises: [], items: [] } as Navigation,
                // ] } as Navigation,
                donneesBaseNavigation(enableTransporteurs)
            ]
        },
        {
            id: NavigationCollaborateurMenuId.douane,
            title: 'Douane',
            visible: false,
            navigation: [
                // { text: 'Déclarations', path: '/douane/declarations', icon: '', permissionsRequises: [], visible: true, items: [] } as Navigation,
                donneesBaseNavigation(enableTransporteurs)
            ]
        },
        {
            id: NavigationCollaborateurMenuId.fretLocal,
            title: 'Fret local',
            visible: false,
            navigation: [
                { text: 'Connaissements', path: '/fret-local/connaissements', icon: 'icon ion-md-boat', visible: parametrage.currentUserType == TypeUtilisateurRuunui.chargeur, permissionsRequises: [], items: [] } as Navigation,
                {
                    text: 'Connaissements', path: null, icon: 'icon ion-md-boat', permissionsRequises: [], visible: parametrage.currentUserType == TypeUtilisateurRuunui.armateur, items: [
                        { text: 'Liste', path: '/fret-local/connaissements-liste', visible: parametrage.currentUserType == TypeUtilisateurRuunui.armateur, permissionsRequises: [], items: [] } as Navigation,
                        // { text: 'Nouveau', path: '/fret-local/nouveau', icon: 'add', visible: parametrage.currentUserType == TypeUtilisateurRuunui.armateur, permissionsRequises: [], items: [] } as Navigation,
                        { text: 'Facturer', path: '/fret-local/facturer', visible: parametrage.currentUserType == TypeUtilisateurRuunui.armateur, permissionsRequises: [], items: [] } as Navigation,
                        { text: 'Export', path: '/fret-local/export', visible: parametrage.currentUserType == TypeUtilisateurRuunui.armateur, permissionsRequises: [], items: [] } as Navigation,
                    ]
                } as Navigation,
                { text: 'Demandes chargeurs', path: '/fret-local/demandes-chargeurs', icon: 'icon ion-md-filing', visible: parametrage.currentUserType == TypeUtilisateurRuunui.armateur, permissionsRequises: [], items: [] } as Navigation,
                { text: 'Voyages', path: '/fret-local/voyages', icon: 'icon ion-md-clipboard', visible: parametrage.currentUserType == TypeUtilisateurRuunui.armateur, permissionsRequises: [], items: [] } as Navigation,
                { text: 'Lieux débarquements', path: '/fret-local/secteurs-geographique', icon: 'map', visible: true, permissionsRequises: [], items: [] } as Navigation,
                { text: 'Nomenclatures', path: '/fret-local/nomenclatures', icon: 'icon ion-md-document', visible: parametrage.currentUserType == TypeUtilisateurRuunui.armateur, permissionsRequises: [], items: [] } as Navigation,
                { text: 'Codes avantages', path: '/fret-local/codes-avantage', icon:'favorites', visible: true, permissionsRequises: [], items: [] } as Navigation,
                { text: 'Synchronisation Bo', path: '/fret-local/synchronisation-back-office', icon: 'download', visible: true, permissionsRequises: [Permissions.canSynchroniserBackoffice], items: [] } as Navigation,
                { text: 'Tarifs fret', path: '/fret-local/tarifs-fret', icon: 'file', visible: true, permissionsRequises: [], items: [] } as Navigation,
                donneesBaseNavigation(enableTransporteurs)
            ]
        },
        {
            id: NavigationCollaborateurMenuId.kpi,
            title: 'KPI',
            visible: false,
            navigation: [
                { text: 'Tableaux de bord', path: '/kpi/dashboards', icon: 'chart', visible: true, permissionsRequises: [], items: [] } as Navigation,
                { text: 'Gestion', path: '/kpi/dashboards/admin', icon: 'datausage', visible: false, permissionsRequises: [], items: [] } as Navigation,
                donneesBaseNavigation(enableTransporteurs)
            ]
        },
        {
            id: NavigationCollaborateurMenuId.comptabilite,
            title: 'Comptabilité',
            visible: false,
            navigation: [
                { text: 'Plan comptable', path: '/comptabilite/comptes-generaux', icon: 'chart', visible: true, permissionsRequises: [], items: [] } as Navigation,
                { text: 'Interface comptable', path: '/comptabilite/interface-comptable', icon: 'icon ion-md-desktop', visible: true, permissionsRequises: [], items: [] } as Navigation,
            ]
        },
        {
            id: NavigationCollaborateurMenuId.support,
            title: 'Support',
            visible: false,
            navigation: [
                { text: 'Support', path: '/support/ticket', icon: 'help', visible: true, permissionsRequises: [], items: [] } as Navigation
            ]
        }
    ];
}

export const navigationPartenaire = (societe: SocieteAvecPermissionsPortailClientDto): Array<NavigationTop> => {
    return [
        {
            id: 0,
            title: '',
            visible: false,
            navigation: [
                { text: 'Profil', path: '/portail-client/information-profil', icon: 'user', permissionsRequises: [], visible: true, items: [] } as Navigation,
                { text: 'Factures', path: '/portail-client/factures', icon: 'file', visible: societe?.portailClientAffichageFactures ?? false, permissionsRequises: [], items: [] } as Navigation,
                { text: 'Demandes', path: '/portail-client/demandes', icon: 'bulletlist', visible: societe?.portailClientAffichageDemandes ?? false, permissionsRequises: [], items: [] } as Navigation,
                { text: 'Interventions', path: '/portail-client/interventions', icon: 'icon ion-md-construct', visible: societe?.portailClientAffichageActivites ?? false, permissionsRequises: [], items: [] } as Navigation,
                // { text: 'Dossiers', path: '/portail-client/contrats', icon: 'icon ion-md-document', visible: societe?.portailClientAffichageDossiers ?? false, permissionsRequises: [], items: [] } as Navigation,
                { text: 'Connaissements', path: '/portail-client/connaissements', icon: 'icon ion-md-boat', visible: societe?.portailClientAffichageConnaissements ?? false, permissionsRequises: [], items: [] } as Navigation,
                { text: 'Catalogue', path: '/portail-client/articles', icon: 'icon ion-md-cart', visible: societe?.portailClientAffichageArticles ?? false, permissionsRequises: [], items: [] } as Navigation
            ]
        }
    ];
};

const navigationLogistique = (parametrage: Parametrage, enableTransporteurs: boolean, featureFlagService: FeatureFlagService): Navigation[] => {
    const stock: Navigation = {
        text: 'Stock', path: null, icon: 'folder', permissionsRequises: [], visible: true, items: [
            { text: 'Etat du stock', path: '/visualisation-stock', visible: true, permissionsRequises: [Permissions.accesVueConsultationStock], items: [] } as Navigation,
            { text: 'Numéros de série', path: '/logistique/registres-numeros-serie', visible: true, permissionsRequises: [], items: [] } as Navigation,
            { text: 'Mouvements', path: '/visualisation-des-mouvements', visible: true, permissionsRequises: [Permissions.accesVueConsultationStock], items: [] } as Navigation,
            { text: 'Valorisation stock', path: '/valorisation-stock', visible: parametrage.currentSocieteManageStock, permissionsRequises: [Permissions.accesVueConsultationStock, Permissions.canAfficherPmp], items: [] } as Navigation,
        ]
    };
    const activites: Navigation = {
        text: 'Activités', path: null, icon: 'folder', permissionsRequises: [], visible: true,
        items: parametrage.currentSocieteManageStock
            ? [
                { text: 'Documents', path: '/logistique/documents', visible: true, permissionsRequises: [Permissions.canAfficherDocumentsLogistiques], items: [] } as Navigation,
                { text: 'Tous les bons', path: '/visualisation-des-bons/all', visible: featureFlagService.isPreviewEnabled(), permissionsRequises: [], items: [] } as Navigation,
                { text: 'Bons', path: '/visualisation-des-bons', visible: true, permissionsRequises: [], items: [] } as Navigation,
                { text: 'Réceptions', path: '/logistique/receptions', visible: featureFlagService.isPreviewEnabled(), permissionsRequises: [], items: [] } as Navigation,
            ] : [
                { text: 'Réceptions', path: '/logistique/receptions', visible: true, permissionsRequises: [], items: [] } as Navigation,
                { text: 'Inventaires', path: '/logistique/inventaires', visible: true, permissionsRequises: [], items: [] } as Navigation,
                { text: 'Préparations', path: '/logistique/preparations', visible: true, permissionsRequises: [], items: [] } as Navigation,
            ]
    };
    const analyses: Navigation = {
        text: 'Analyses', path: null, icon: 'folder', permissionsRequises: [], visible: true,
        items: [
            { text: 'Stock dormant', path: '/logistique/analyse/dormant', visible: true, permissionsRequises: [], items: [] } as Navigation
        ]
    }

    const parametres: Navigation =
    {
        text: 'Paramètres', path: null, icon: 'preferences', permissionsRequises: [], visible: true, expanded: true, items: [
            { text: 'Lieux de stockage', path: '/logistique/lieux-de-stockage', visible: true, permissionsRequises: [Permissions.canGererLieuStockages], items: [] } as Navigation,
            { text: 'Référencement', path: '/logistique/code-barres', visible: true, permissionsRequises: [], items: [] } as Navigation,
            { text: 'Terminaux', path: '/logistique/terminaux', visible: true, permissionsRequises: [Permissions.canManageTerminaux], items: [] } as Navigation,
            { text: 'Back Office', path: '/logistique/traitements', visible: true, permissionsRequises: [], items: [] } as Navigation,
        ]
    };

    const navigations: Navigation[] = [];

    if (parametrage.currentSocieteManageStock) {
        navigations.push(stock);
    }
    navigations.push(activites);
    navigations.push(analyses);
    navigations.push(parametres);
    navigations.push(donneesBaseNavigation(enableTransporteurs))

    return navigations;
};
